import { useMemo, useState } from "react";
import { usePoll } from "@/hooks/usePoll";
import { PageHeader } from "@/components/Badges";

const CCY = ["ALL", "USD", "EUR", "GBP", "JPY", "AUD", "NZD", "CAD", "CHF"];

export default function CalendarPage() {
  const { data, loading } = usePoll("/calendar", 300000);
  const [ccy, setCcy] = useState("ALL");
  const [impact, setImpact] = useState("ALL");
  const groups = useMemo(() => {
    const g = {};
    (data?.events || []).filter((e) => (ccy === "ALL" || e.currency === ccy) && (impact === "ALL" || e.impact === impact))
      .forEach((e) => { const d = e.date.slice(0, 10); (g[d] = g[d] || []).push(e); });
    return Object.entries(g);
  }, [data, ccy, impact]);
  const now = new Date().toISOString();
  return (
    <div data-testid="calendar-page">
      <PageHeader title="Economic Calendar" sub={`Source: ${data?.source === "fmp" ? "Financial Modeling Prep" : "Seeded schedule (FMP plan lacks calendar access)"} · times in UTC`} />
      <div className="flex flex-wrap gap-2 mb-4">
        {CCY.map((c) => <button key={c} onClick={() => setCcy(c)} data-testid={`calendar-ccy-${c.toLowerCase()}`}
          className={`px-3 py-1.5 text-xs num rounded-sm border transition-colors ${ccy === c ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{c}</button>)}
        <span className="w-4" />
        {["ALL", "High", "Medium", "Low"].map((i) => <button key={i} onClick={() => setImpact(i)} data-testid={`calendar-impact-${i.toLowerCase()}`}
          className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${impact === i ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-500 hover:text-zinc-900"}`}>{i}</button>)}
      </div>
      {loading && !data && <p className="label-xs">Loading events…</p>}
      <div className="space-y-4">
        {groups.map(([day, evs]) => (
          <section key={day} className="panel" data-testid={`calendar-day-${day}`}>
            <h3 className="px-4 py-2.5 border-b border-zinc-200 font-display font-bold text-sm">{new Date(day + "T00:00:00Z").toUTCString().slice(0, 16)}</h3>
            <table className="w-full text-sm">
              <tbody>
                {evs.map((e, i) => (
                  <tr key={i} className={`border-b border-zinc-100 ${e.date < now ? "opacity-50" : ""}`} data-testid={`calendar-event-${day}-${i}`}>
                    <td className="px-4 py-2 num text-xs text-zinc-500 w-16">{e.date.slice(11, 16)}</td>
                    <td className="px-2 py-2 num text-xs font-bold w-12">{e.currency}</td>
                    <td className="px-2 py-2 w-20"><span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 border rounded-sm ${e.impact === "High" ? "border-[#DC2626]/40 text-[#DC2626]" : e.impact === "Medium" ? "border-[#D97706]/40 text-[#D97706]" : "border-zinc-300 text-zinc-500"}`}>{e.impact}</span></td>
                    <td className="px-2 py-2 text-zinc-800">{e.event}</td>
                    <td className="px-2 py-2 num text-xs text-zinc-500 whitespace-nowrap">Prev {e.previous ?? "—"}</td>
                    <td className="px-2 py-2 num text-xs text-zinc-500 whitespace-nowrap">Est {e.estimate ?? "—"}</td>
                    <td className="px-4 py-2 num text-xs text-zinc-900 whitespace-nowrap">Act {e.actual ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </div>
  );
}
